import { useCallback, useEffect, useRef, useState } from 'react';

interface InterruptibleTransitionOptions {
    /** Called once after the content is gone, also when a close skips the leave transition. */
    afterLeave?: () => void;
}

export interface InterruptibleTransition {
    /** Pass as the `key` of the root `Transition`. */
    key: number;
    appear: true;
    afterEnter: () => void;
    afterLeave: () => void;
}

/**
 * Props for a HeadlessUI root `Transition` whose `show` can flip while a
 * transition is still running.
 *
 * HeadlessUI 2.2 can get stuck in that case: the reversed transition never
 * reports its end, so a Dialog stays mounted as an invisible full-screen
 * overlay that blocks every click. A flip during a running transition
 * therefore remounts the `Transition` (new `key`), so every transition starts
 * from a settled state. A close during the enter transition removes the
 * content at once and calls `afterLeave` itself. An open during the leave
 * transition restarts the enter transition (`appear`) and does not call
 * `afterLeave`, because the content never left.
 */
export function useInterruptibleTransition(
    show: boolean,
    { afterLeave }: InterruptibleTransitionOptions = {}
): InterruptibleTransition {
    const [transitionKey, setTransitionKey] = useState(0);
    const [runningKey, setRunningKey] = useState<number | null>(show ? 0 : null);
    const [previousShow, setPreviousShow] = useState(show);
    const [skippedLeaveKey, setSkippedLeaveKey] = useState<number | null>(null);
    const reportedLeaveKey = useRef<number | null>(null);

    if (show !== previousShow) {
        setPreviousShow(show);
        if (runningKey === null) {
            setRunningKey(transitionKey);
        } else {
            // A fresh Transition that mounts closed has nothing to animate.
            const nextKey = transitionKey + 1;
            setTransitionKey(nextKey);
            setRunningKey(show ? nextKey : null);
            if (!show) {
                setSkippedLeaveKey(nextKey);
            }
        }
    }

    useEffect(() => {
        if (skippedLeaveKey !== null && reportedLeaveKey.current !== skippedLeaveKey) {
            reportedLeaveKey.current = skippedLeaveKey;
            afterLeave?.();
        }
    }, [skippedLeaveKey, afterLeave]);

    const handleAfterEnter = useCallback((): void => {
        setRunningKey((current) => (current === transitionKey ? null : current));
    }, [transitionKey]);

    const handleAfterLeave = useCallback((): void => {
        setRunningKey((current) => (current === transitionKey ? null : current));
        afterLeave?.();
    }, [transitionKey, afterLeave]);

    return {
        key: transitionKey,
        appear: true,
        afterEnter: handleAfterEnter,
        afterLeave: handleAfterLeave,
    };
}
